import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { adminService } from '../../services/adminService';
import { UserPlus, Edit, MessageCircle } from 'lucide-react';

const AdminFakeProfilesPage = () => {
  const [dummies, setDummies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState({ display_name: '', city: '' });
  const [addError, setAddError] = useState('');

  const load = () => {
    setLoading(true);
    adminService.listDummyProfiles().then((data) => {
      setDummies(data || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const handleAdd = async (e) => {
    e.preventDefault();
    setAddError('');
    setAdding(true);
    try {
      const userId = await adminService.createDummyUser(addForm.display_name, addForm.city);
      setShowAdd(false);
      setAddForm({ display_name: '', city: '' });
      load();
      window.location.href = `/admin/fake-profiles/${userId}/edit`;
    } catch (err) {
      setAddError(err.message || 'Failed to create');
    }
    setAdding(false);
  };

  return (
    <div>
      <h1 className="font-heading font-bold text-2xl text-slate-900 mb-4">Fake profiles</h1>
      <p className="text-slate-600 mb-4">Manage dummy profiles and chat as them.</p>
      <div className="mb-4 p-3 rounded-lg bg-slate-100 border border-slate-200 text-sm text-slate-700">
        <strong>Initial launch:</strong> To add 6 fake female profiles at once, run from project root:{" "}
        <code className="bg-slate-200 px-1 rounded">node --env-file=.env scripts/seed-dummy-profiles.js</code>
        {" "}(requires <code className="bg-slate-200 px-1 rounded">SUPABASE_SERVICE_ROLE_KEY</code> in .env).
      </div>
      <div className="mb-4">
        {!showAdd ? (
          <button
            type="button"
            onClick={() => setShowAdd(true)}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-primary text-white text-sm font-medium hover:bg-primary-dark"
          >
            <UserPlus className="h-4 w-4" />
            Add dummy profile
          </button>
        ) : (
          <form onSubmit={handleAdd} className="p-4 rounded-xl bg-white border border-slate-200 max-w-md space-y-3">
            <input
              type="text"
              placeholder="Display name"
              value={addForm.display_name}
              onChange={(e) => setAddForm((f) => ({ ...f, display_name: e.target.value }))}
              className="w-full px-3 py-2 rounded-lg border border-slate-300 text-slate-800"
              required
            />
            <input
              type="text"
              placeholder="City"
              value={addForm.city}
              onChange={(e) => setAddForm((f) => ({ ...f, city: e.target.value }))}
              className="w-full px-3 py-2 rounded-lg border border-slate-300 text-slate-800"
            />
            {addError && <p className="text-sm text-red-600">{addError}</p>}
            <div className="flex gap-2">
              <button type="submit" disabled={adding} className="px-4 py-2 rounded-lg bg-primary text-white text-sm font-medium disabled:opacity-50">
                {adding ? 'Creating...' : 'Create'}
              </button>
              <button type="button" onClick={() => { setShowAdd(false); setAddError(''); }} className="px-4 py-2 rounded-lg border border-slate-300 text-slate-700 text-sm">
                Cancel
              </button>
            </div>
          </form>
        )}
      </div>
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-10 w-10 border-2 border-primary border-t-transparent" />
        </div>
      ) : dummies.length === 0 ? (
        <div className="p-8 rounded-xl bg-white border border-slate-200 text-center text-slate-500">
          No dummy profiles. Add one above.
        </div>
      ) : (
        <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left p-3 font-medium text-slate-700">Profile</th>
                <th className="text-left p-3 font-medium text-slate-700">City</th>
                <th className="text-left p-3 font-medium text-slate-700">Visible</th>
                <th className="text-left p-3 font-medium text-slate-700">Actions</th>
              </tr>
            </thead>
            <tbody>
              {dummies.map((d) => (
                <tr key={d.id} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="p-3 flex items-center gap-2">
                    <img
                      src={d.avatar_url || '/default-avatar.svg'}
                      alt=""
                      className="w-8 h-8 rounded-full object-cover bg-slate-200"
                    />
                    {d.display_name || d.username || d.id}
                  </td>
                  <td className="p-3">{d.city || '—'}</td>
                  <td className="p-3">{d.hide_profile ? 'Hidden' : 'Visible'}</td>
                  <td className="p-3 flex gap-2">
                    <Link
                      to={`/admin/fake-profiles/${d.id}/edit`}
                      className="inline-flex items-center gap-1 text-primary hover:underline"
                    >
                      <Edit className="h-4 w-4" />
                      Edit
                    </Link>
                    <Link
                      to={`/admin/fake-profiles/${d.id}/chats`}
                      className="inline-flex items-center gap-1 text-primary hover:underline"
                    >
                      <MessageCircle className="h-4 w-4" />
                      Chats
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AdminFakeProfilesPage;
